import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { NumberRangeInput } from "@/components/ui/number-range-input";
import { TrashIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BaseFilterProps, Filter, FilterData } from "../_types";

interface NumberRangeFilterConfig {
  id: string;
  labelKey: string;
}

function createNumberRangeFilter(config: NumberRangeFilterConfig) {
  class NumberRangeFilter extends Filter<Record<string, never>, Record<string, never>> {
    readonly id: string = config.id;
    readonly labelSingular = config.labelKey;
    readonly labelPlural = config.labelKey;
    readonly placeholder = config.labelKey;
    readonly searchPlaceholder = "";
    readonly emptyText = "";
    body = {};
    parameters = {};

    constructor({ id }: { id?: string } = {}) {
      super();
      if (id) this.id = id;
    }

    getData(): FilterData {
      return { options: [], isLoading: false, error: null };
    }

    render(props: BaseFilterProps) {
      return <NumberRangeFilterComponent labelKey={config.labelKey} {...props} />;
    }
  }

  return NumberRangeFilter;
}

function NumberRangeFilterComponent({
  labelKey,
  selectedValues,
  onSelectionChange,
  disabled,
}: BaseFilterProps & { labelKey: string }) {
  const { t } = useTranslation(["common", "fab"]);

  const min = selectedValues[0] ?? "";
  const max = selectedValues[1] ?? "";

  const handleMinChange = (value: string) => {
    onSelectionChange(value === "" && max === "" ? [] : [value, max]);
  };

  const handleMaxChange = (value: string) => {
    onSelectionChange(min === "" && value === "" ? [] : [min, value]);
  };

  const handleClear = () => {
    onSelectionChange([]);
  };

  const hasValues = min !== "" || max !== "";
  const label = labelKey.includes(":") ? t(labelKey) : labelKey;

  return (
    <div className="flex flex-col">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center gap-2">
        <NumberRangeInput
          min={min}
          max={max}
          onMinChange={handleMinChange}
          onMaxChange={handleMaxChange}
          disabled={disabled}
          className="min-w-0 flex-1"
        />
        <Button
          variant={ButtonVariant.Outlined}
          colorVariant={ButtonColorVariant.Secondary}
          size={ButtonSize.Icon}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClear();
          }}
          className="shrink-0"
          disabled={!hasValues || disabled}
          aria-label={t("common:Remove")}
        >
          <TrashIcon className="size-4" />
        </Button>
      </div>
    </div>
  );
}

export const VehicleValuationTradeGrossRangeFilter = createNumberRangeFilter({
  id: "tradeGross",
  labelKey: "fab:TradeGrossRange",
});

export const VehicleValuationSalesGrossRangeFilter = createNumberRangeFilter({
  id: "salesGross",
  labelKey: "fab:SalesGrossRange",
});

export const VehicleValuationMarginGrossRangeFilter = createNumberRangeFilter({
  id: "marginGross",
  labelKey: "fab:MarginGrossRange",
});
